import { HTMLAttributes } from 'react'

import Container from '@/components/base/Container'
import Cta from '@/components/base/Cta'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import MediaParallax from '@/components/base/MediaParallax'
import Motion from '@/components/base/Motion'
import Typography, { TypographyProps } from '@/components/base/Typography'

import { imageParagraph } from './animations'
import { cn } from '@/lib/utils'
import { WPImage, WPLink, WPPageData, WPVideo } from '@/types'

export type ImageParagraphProps = HTMLAttributes<HTMLElement> &
  Partial<{
    paragraph: {
      pretitle: string
      title: string
      title_component: TypographyProps['component']
      paragraph: string
      button: WPLink
    }
    media: {
      poster: WPImage
      video: WPVideo
    }
    alignment: 'left' | 'right'
    vertical: boolean
    is_in_lightbox: boolean
    theme: WPPageData['acf']['theme']
  }>

const ImageParagraph = ({
  alignment = 'left',
  media,
  paragraph,
  theme,
  vertical,
  is_in_lightbox = false,
  ...props
}: ImageParagraphProps) => {
  return (
    <Motion animation={imageParagraph}>
      {(motionRef) => (
        <section
          ref={motionRef}
          {...props}
          className={cn([
            {
              'bg-dark-100 text-dark-500': theme === 'light',
              'bg-dark-600 text-white': theme === 'dark'
            }
          ])}
        >
          <Container>
            <Grid className="gap-y-[6.4rem]">
              {paragraph ? (
                <div
                  className={cn([
                    'col-span-4 flex flex-col gap-[1.6rem]',
                    'md:col-span-6',
                    'lg:col-span-5 lg:gap-[3.2rem]',
                    {
                      'lg:col-start-2': alignment === 'left',
                      'lg:col-start-7': alignment === 'right',
                      'justify-center': vertical
                    }
                  ])}
                >
                  {paragraph.pretitle ? (
                    <Typography
                      variant="label"
                      className="font-extrabold"
                      data-motion-pretitle
                    >
                      {paragraph.pretitle}
                    </Typography>
                  ) : null}
                  {paragraph.title ? (
                    <Typography
                      variant="h3"
                      className="!font-normal [&_strong]:font-bold"
                      component={paragraph.title_component}
                      data-motion-title
                    >
                      {paragraph.title}
                    </Typography>
                  ) : null}
                  {paragraph.paragraph ? (
                    <Typography variant="body" data-motion-paragraph>
                      {paragraph.paragraph}
                    </Typography>
                  ) : null}
                  {paragraph.button ? (
                    <Cta
                      link={paragraph.button}
                      theme="light"
                      data-motion-cta
                    />
                  ) : null}
                </div>
              ) : null}
              {media ? (
                <div
                  className={cn([
                    'col-span-4',
                    'md:col-span-6',
                    'lg:col-span-4',
                    {
                      'lg:col-start-8': alignment === 'left',
                      '-order-1 lg:col-start-2': alignment === 'right'
                    }
                  ])}
                >
                  <MediaParallax
                    className={cn({
                      'aspect-h-4 aspect-w-6': !vertical,
                      'aspect-h-5 aspect-w-4': vertical
                    })}
                  >
                    {(ref: React.RefObject<HTMLElement>) =>
                      media.video.url ? (
                        <video
                          ref={ref as React.RefObject<HTMLVideoElement>} // Assegna qui la ref al video
                          src={media.video.url.toString()}
                          poster={media.poster.sizes.large.url}
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="h-full w-full object-cover"
                          data-motion-image
                        />
                      ) : (
                        <div data-gallery-wrapper={is_in_lightbox}>
                          <Image
                            ref={ref as React.RefObject<HTMLImageElement>}
                            source={media.poster}
                            size="large"
                            data-motion-image
                            data-gallery={is_in_lightbox}
                          />
                        </div>
                      )
                    }
                  </MediaParallax>
                </div>
              ) : null}
            </Grid>
          </Container>
        </section>
      )}
    </Motion>
  )
}

export default ImageParagraph
